if (!customElements.get('product-drawer')) {
  customElements.define(
    'product-drawer',
    class ProductDrawer extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector('.product-drawer__overlay');
        this.panel = this.querySelector('.product-drawer__panel');
        this.closeButton = this.querySelector('.product-drawer__close');
        this.body = this.querySelector('.product-drawer__body');
        this.content = this.querySelector('.product-drawer__content');
        this.loadingState = this.querySelector('.product-drawer__loading');
        this.status = this.querySelector('.product-drawer__status');
        this.activeElement = null;
        this.abortController = null;
        this.ignoreNextPopState = false;
        this.productCache = new Map();
        this.pendingRequests = new Map();
        this.assetPromises = new Map();
        this.imageObserver = new MutationObserver(() => this.initImagePreloaders());
        this.idlePrefetchLimit = 4;
        this.lastTrackedProductView = null;
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleIntent = this.handleIntent.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleContentLoad = this.handleContentLoad.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
      }

      connectedCallback() {
        this.overlay.addEventListener('click', () => this.close());
        this.closeButton.addEventListener('click', () => this.close());
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('pointerdown', this.handleIntent, { passive: true });
        document.addEventListener('pointerover', this.handleIntent);
        document.addEventListener('focusin', this.handleIntent);
        document.addEventListener('touchstart', this.handleIntent, { passive: true });
        document.addEventListener('keyup', this.handleKeyUp);
        window.addEventListener('popstate', this.handlePopState);
        this.content.addEventListener('load', this.handleContentLoad, true);
        this.imageObserver.observe(this.content, { childList: true, subtree: true });
        this.scheduleIdlePrefetch();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('pointerdown', this.handleIntent);
        document.removeEventListener('pointerover', this.handleIntent);
        document.removeEventListener('focusin', this.handleIntent);
        document.removeEventListener('touchstart', this.handleIntent);
        document.removeEventListener('keyup', this.handleKeyUp);
        window.removeEventListener('popstate', this.handlePopState);
        this.content.removeEventListener('load', this.handleContentLoad, true);
        this.imageObserver.disconnect();
      }

      handleKeyUp(event) {
        if (event.code === 'Escape' && this.classList.contains('active')) this.close();
      }

      handleContentLoad(event) {
        if (event.target?.tagName !== 'IMG') return;
        this.markImageLoaded(event.target);
      }

      handlePopState(event) {
        if (!this.classList.contains('active')) return;

        if (this.ignoreNextPopState) {
          this.ignoreNextPopState = false;
          this.finishClose();
          return;
        }

        if (!event.state?.productDrawer) {
          this.finishClose();
        }
      }

      handleDocumentClick(event) {
        if (event.defaultPrevented || this.shouldIgnoreEvent(event)) return;

        const explicitTrigger = event.target.closest('[data-product-drawer-trigger]');
        if (explicitTrigger) {
          event.preventDefault();
          this.open({
            opener: explicitTrigger,
            productUrl: explicitTrigger.getAttribute('href') || explicitTrigger.dataset.productUrl,
          });
          return;
        }

        const card = event.target.closest('.product-card-wrapper[data-product-url]');
        if (!card || event.target.closest(this.interactiveSelector())) return;

        event.preventDefault();
        this.open({
          opener: card,
          productUrl: card.dataset.productUrl,
        });
      }

      handleIntent(event) {
        const productUrl = this.getProductUrlFromTarget(event.target);
        if (!productUrl) return;

        this.prefetch(productUrl);
      }

      shouldIgnoreEvent(event) {
        return (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.target.closest('.quick-add, quick-add-bulk, product-form, modal-opener')
        );
      }

      interactiveSelector() {
        return 'a, button, input, select, textarea, summary, label, [role="button"]';
      }

      async open({ opener, productUrl }) {
        if (!productUrl) return;

        const normalizedProductUrl = this.normalizeProductUrl(productUrl);
        const drawerWasInactive = !this.classList.contains('active');

        if (drawerWasInactive) {
          window.history.pushState({ productDrawer: true }, '', normalizedProductUrl);
        } else {
          window.history.replaceState({ productDrawer: true }, '', normalizedProductUrl);
        }

        this.activeElement =
          opener?.matches?.('a, button, input, select, textarea, [tabindex]') ? opener : document.activeElement;
        this.classList.add('active');
        document.body.classList.add('overflow-hidden');
        trapFocus(this, this.closeButton);

        opener?.setAttribute('aria-expanded', 'true');

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.showLoadingState();

        try {
          const productData = await this.getProductData(normalizedProductUrl, this.abortController.signal);

          if (!this.classList.contains('active')) return;

          this.renderProduct(productData, normalizedProductUrl);
          this.hideLoadingState();
          this.loadRelatedProducts(normalizedProductUrl, this.abortController.signal);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
            window.location.href = normalizedProductUrl;
          }
        }
      }

      close() {
        if (this.classList.contains('active') && window.history.state?.productDrawer) {
          this.ignoreNextPopState = true;
          window.history.back();
          return;
        }

        this.finishClose();
      }

      finishClose() {
        this.abortController?.abort();
        this.pauseDrawerMedia();
        this.removeDrawerProductModals();
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.activeElement);
        this.activeElement?.removeAttribute('aria-expanded');
        this.lastTrackedProductView = null;
        this.playHomepageVideos();
      }

      pauseDrawerMedia() {
        this.content.querySelectorAll('.js-youtube').forEach((video) => {
          video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        this.content.querySelectorAll('.js-vimeo').forEach((video) => {
          video.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
        this.content.querySelectorAll('video').forEach((video) => video.pause());
        this.content.querySelectorAll('product-model').forEach((model) => {
          if (model.modelViewerUI) model.modelViewerUI.pause();
        });
      }

      playHomepageVideos() {
        document.querySelectorAll('.hero-video-section video').forEach((video) => {
          video.loop = true;
          video.play().catch(() => {});
        });
      }

      showLoadingState() {
        this.removeDrawerProductModals();
        this.content.innerHTML = '';
        this.loadingState.hidden = false;
        if (this.body) this.body.scrollTop = 0;
      }

      hideLoadingState() {
        this.loadingState.hidden = true;
      }

      async preprocessHTML(productElement) {
        await this.loadAssets(productElement);
        this.syncColorClasses(productElement);
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.prepareDrawerImages(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      getProductUrlFromTarget(target) {
        const explicitTrigger = target?.closest?.('[data-product-drawer-trigger]');
        if (explicitTrigger) return explicitTrigger.getAttribute('href') || explicitTrigger.dataset.productUrl;

        return target?.closest?.('.product-card-wrapper[data-product-url]')?.dataset.productUrl;
      }

      normalizeProductUrl(productUrl) {
        return new URL(productUrl, window.location.origin).toString();
      }

      prefetch(productUrl) {
        const normalizedProductUrl = this.normalizeProductUrl(productUrl);
        if (this.productCache.has(normalizedProductUrl) || this.pendingRequests.has(normalizedProductUrl)) return;

        const request = this.fetchMainProduct(normalizedProductUrl)
          .then((productData) => {
            this.productCache.set(normalizedProductUrl, productData);
            return productData;
          })
          .catch(() => null)
          .finally(() => this.pendingRequests.delete(normalizedProductUrl));

        this.pendingRequests.set(normalizedProductUrl, request);
      }

      scheduleIdlePrefetch() {
        const prefetchVisibleCards = () => {
          const productUrls = Array.from(document.querySelectorAll('.product-card-wrapper[data-product-url]'))
            .filter((card) => {
              const rect = card.getBoundingClientRect();
              return rect.bottom > 0 && rect.top < window.innerHeight;
            })
            .map((card) => card.dataset.productUrl)
            .filter(Boolean)
            .slice(0, this.idlePrefetchLimit);

          productUrls.forEach((productUrl) => this.prefetch(productUrl));
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(prefetchVisibleCards, { timeout: 2500 });
        } else {
          window.setTimeout(prefetchVisibleCards, 1200);
        }
      }

      async getProductData(productUrl, signal) {
        if (this.productCache.has(productUrl)) return this.productCache.get(productUrl);

        if (this.pendingRequests.has(productUrl)) {
          const pendingProduct = await this.pendingRequests.get(productUrl);
          if (pendingProduct) return pendingProduct;
        }

        const request = this.fetchMainProduct(productUrl, signal)
          .then((productData) => {
            this.productCache.set(productUrl, productData);
            return productData;
          })
          .finally(() => this.pendingRequests.delete(productUrl));

        this.pendingRequests.set(productUrl, request);
        return request;
      }

      async fetchMainProduct(productUrl, signal) {
        try {
          const sections = await this.fetchSections(productUrl, ['main'], signal);
          const productElement = this.extractProductElement(sections.main);

          if (!productElement) throw new Error('Missing main product section');

          await this.preprocessHTML(productElement);

          return {
            html: productElement.outerHTML,
            relatedHtml: '',
            relatedLoaded: false,
            title: productElement.querySelector('h1, h2')?.textContent?.trim() || 'Product loaded',
          };
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          return this.fetchProductPage(productUrl, signal);
        }
      }

      async loadRelatedProducts(productUrl, signal) {
        const cachedProduct = this.productCache.get(productUrl);
        if (!cachedProduct || cachedProduct.relatedLoaded) {
          if (cachedProduct?.relatedHtml) this.appendRelatedProducts(cachedProduct.relatedHtml);
          return;
        }

        try {
          const sections = await this.fetchSections(productUrl, ['related-products'], signal);
          const relatedHtml = this.extractRelatedHTML(sections['related-products']);
          cachedProduct.relatedHtml = relatedHtml;
          cachedProduct.relatedLoaded = true;
          this.productCache.set(productUrl, cachedProduct);

          if (this.classList.contains('active')) this.appendRelatedProducts(cachedProduct.relatedHtml);
        } catch (error) {
          if (error.name !== 'AbortError') cachedProduct.relatedLoaded = true;
        }
      }

      async fetchSections(productUrl, sectionIds, signal) {
        const requestUrl = new URL(productUrl);
        requestUrl.searchParams.set('sections', sectionIds.join(','));

        const response = await fetch(requestUrl.toString(), {
          signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Failed to load product sections: ${response.status}`);

        return response.json();
      }

      async fetchProductPage(productUrl, signal) {
        const response = await fetch(productUrl, { signal });
        if (!response.ok) throw new Error(`Failed to load product page: ${response.status}`);

        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('product-info');

        if (!productElement) throw new Error('Missing product info on product page');

        await this.preprocessHTML(productElement);

        return {
          html: productElement.outerHTML,
          relatedHtml: this.extractRelatedHTML(
            responseHTML.querySelector('product-recommendations.related-products')?.closest('.shopify-section')?.outerHTML
          ),
          relatedLoaded: true,
          title: productElement.querySelector('h1, h2')?.textContent?.trim() || 'Product loaded',
        };
      }

      extractProductElement(markup) {
        if (!markup) return null;

        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        return documentFragment.querySelector('product-info');
      }

      extractRelatedHTML(markup) {
        if (!markup) return '';

        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        documentFragment.querySelectorAll('script').forEach((script) => script.remove());

        const sectionMarkup = documentFragment.body.innerHTML.trim();
        if (!sectionMarkup) return '';

        return documentFragment.querySelector('.shopify-section') ? sectionMarkup : `<div class="shopify-section">${sectionMarkup}</div>`;
      }

      renderProduct(productData, productUrl) {
        this.removeDrawerProductModals();
        HTMLUpdateUtility.setInnerHTML(this.content, productData.html);
        this.appendRelatedProducts(productData.relatedHtml);
        this.initImagePreloaders();
        this.status.textContent = productData.title;
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
        this.trackProductView(productUrl);
      }

      appendRelatedProducts(relatedHtml) {
        if (!relatedHtml || this.content.querySelector('.related-products')) return;

        this.content.insertAdjacentHTML('beforeend', relatedHtml);
      }

      trackProductView(productUrl) {
        const analyticsData = this.getProductAnalyticsData(productUrl);
        if (!analyticsData) return;

        const trackingKey = `${analyticsData.item.item_id}:${analyticsData.page_location}`;
        if (this.lastTrackedProductView === trackingKey) return;
        this.lastTrackedProductView = trackingKey;

        this.sendAnalyticsEvent('page_view', {
          page_title: analyticsData.page_title,
          page_location: analyticsData.page_location,
          page_path: analyticsData.page_path,
        });

        const ecommerceParams = { items: [analyticsData.item] };
        if (analyticsData.currency) ecommerceParams.currency = analyticsData.currency;
        if (analyticsData.value !== undefined) ecommerceParams.value = analyticsData.value;

        this.sendAnalyticsEvent('view_item', ecommerceParams);
        this.sendFacebookPixelViewContent(analyticsData);

        document.dispatchEvent(
          new CustomEvent('product-drawer:view-item', {
            bubbles: true,
            detail: analyticsData,
          })
        );
      }

      getProductAnalyticsData(productUrl) {
        const productInfo = this.content.querySelector('product-info');
        if (!productInfo) return null;

        const pageUrl = new URL(productUrl || productInfo.dataset.url || window.location.href, window.location.origin);
        const product = this.getStructuredProductData(productInfo);
        const selectedVariant = this.getSelectedVariantData(productInfo);
        const offer = this.getStructuredOffer(product, selectedVariant);
        const productId = productInfo.dataset.productId;
        const variantId =
          selectedVariant?.id ||
          productInfo.querySelector('product-form input[name="id"], form[action*="/cart/add"] input[name="id"]')?.value;
        const price = this.normalizePrice(selectedVariant?.price, offer?.price);
        const currency = offer?.priceCurrency || window.Shopify?.currency?.active || window.Shopify?.currency?.currency || '';
        const item = {
          item_id: String(selectedVariant?.sku || variantId || productId || pageUrl.pathname),
          item_name:
            product?.name ||
            productInfo.querySelector('.product__title h1, .product__title h2, h1, h2')?.textContent?.trim() ||
            document.title,
          item_brand: this.getBrandName(product?.brand),
          item_variant:
            selectedVariant?.title && selectedVariant.title !== 'Default Title' ? selectedVariant.title : undefined,
          price,
          quantity: 1,
        };

        Object.keys(item).forEach((key) => item[key] === undefined && delete item[key]);

        return {
          currency,
          value: price,
          item,
          page_title: item.item_name,
          page_location: pageUrl.toString(),
          page_path: `${pageUrl.pathname}${pageUrl.search}`,
        };
      }

      getStructuredProductData(productInfo) {
        const structuredScripts = productInfo.querySelectorAll('script[type="application/ld+json"]');

        for (const script of structuredScripts) {
          const data = this.parseJSON(script.textContent);
          const product = this.findStructuredProduct(data);
          if (product) return product;
        }

        return null;
      }

      findStructuredProduct(data) {
        if (!data || typeof data !== 'object') return null;

        if (Array.isArray(data)) {
          for (const item of data) {
            const product = this.findStructuredProduct(item);
            if (product) return product;
          }
          return null;
        }

        const type = data['@type'];
        if (type === 'Product' || (Array.isArray(type) && type.includes('Product'))) return data;

        return this.findStructuredProduct(data['@graph']);
      }

      getSelectedVariantData(productInfo) {
        return this.parseJSON(productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent);
      }

      getStructuredOffer(product, selectedVariant) {
        const offers = product?.offers;
        if (!offers) return null;

        const offerList = Array.isArray(offers) ? offers : [offers];
        if (!selectedVariant) return offerList[0];

        return (
          offerList.find((offer) => selectedVariant.sku && offer.sku === selectedVariant.sku) ||
          offerList.find((offer) => offer.url && String(offer.url).includes(`variant=${selectedVariant.id}`)) ||
          offerList[0]
        );
      }

      getBrandName(brand) {
        if (!brand) return undefined;
        if (typeof brand === 'string') return brand;
        return brand.name;
      }

      normalizePrice(variantPrice, offerPrice) {
        if (Number.isFinite(variantPrice)) return variantPrice / 100;

        const parsedOfferPrice = parseFloat(offerPrice);
        return Number.isFinite(parsedOfferPrice) ? parsedOfferPrice : undefined;
      }

      parseJSON(value) {
        if (!value) return null;

        try {
          return JSON.parse(value);
        } catch (error) {
          return null;
        }
      }

      sendAnalyticsEvent(eventName, params) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, params);
          return;
        }

        if (!Array.isArray(window.dataLayer)) window.dataLayer = [];

        if (eventName === 'view_item') {
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: eventName,
            ecommerce: params,
          });
          return;
        }

        window.dataLayer.push({
          event: eventName,
          ...params,
        });
      }

      sendFacebookPixelViewContent(analyticsData) {
        if (typeof window.fbq !== 'function') return;

        const pixelParams = {
          content_ids: [analyticsData.item.item_id],
          content_name: analyticsData.item.item_name,
          content_type: 'product',
        };

        if (analyticsData.currency) pixelParams.currency = analyticsData.currency;
        if (analyticsData.value !== undefined) pixelParams.value = analyticsData.value;

        window.fbq('track', 'ViewContent', pixelParams);
      }

      syncColorClasses(productElement) {
        [...this.panel.classList]
          .filter((className) => className.startsWith('color-') || className === 'gradient')
          .forEach((className) => this.panel.classList.remove(className));

        productElement.classList.forEach((className) => {
          if (className.startsWith('color-') || className === 'gradient') this.panel.classList.add(className);
        });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());
      }

      removeDrawerProductModals() {
        document
          .querySelectorAll('product-modal[id^="ProductModal-product-drawer-"]')
          .forEach((modal) => modal.remove());
      }

      loadAssets(productElement) {
        const assetLoaders = [];

        productElement.querySelectorAll('link[rel="stylesheet"][href]').forEach((stylesheet) => {
          const href = stylesheet.href;
          assetLoaders.push(
            this.loadAssetOnce(`style:${href}`, () => this.appendStylesheet(href)).catch((error) => console.error(error))
          );
          stylesheet.remove();
        });

        productElement.querySelectorAll('script[src]').forEach((script) => {
          const src = script.src;
          assetLoaders.push(
            this.loadAssetOnce(`script:${src}`, () => this.appendScript(src, script.defer)).catch((error) => console.error(error))
          );
          script.remove();
        });

        return Promise.all(assetLoaders);
      }

      loadAssetOnce(key, loader) {
        if (!this.assetPromises.has(key)) {
          this.assetPromises.set(
            key,
            loader().catch((error) => {
              this.assetPromises.delete(key);
              throw error;
            })
          );
        }

        return this.assetPromises.get(key);
      }

      appendStylesheet(href) {
        const existingStylesheet = document.head.querySelector(`link[rel="stylesheet"][href="${href}"]`);
        if (existingStylesheet) return Promise.resolve();

        return new Promise((resolve, reject) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = () => resolve();
          link.onerror = reject;
          document.head.appendChild(link);
        });
      }

      appendScript(src, defer = false) {
        const existingScript = document.head.querySelector(`script[src="${src}"]`);
        if (existingScript) return Promise.resolve();

        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          if (defer) script.defer = true;
          script.onload = () => {
            script.dataset.loaded = 'true';
            resolve();
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const newId = `product-drawer-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(sectionId, newId);

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(sectionId)) {
            productElement.setAttribute(attribute.name, attribute.value.replaceAll(sectionId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1200px) 42rem, (min-width: 750px) calc((100vw - 10rem) / 2), calc(100vw - 3.2rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('42rem', '36rem');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('42rem', '30rem');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      prepareDrawerImages(productElement) {
        const mediaImages = Array.from(productElement.querySelectorAll('.product__media img'));
        if (!mediaImages.length) return;

        mediaImages.forEach((img, index) => {
          img.setAttribute('decoding', 'async');

          if (index === 0) {
            img.setAttribute('loading', 'eager');
            img.setAttribute('fetchpriority', 'high');
          } else {
            img.setAttribute('loading', 'lazy');
            img.removeAttribute('fetchpriority');
          }
        });
      }

      initImagePreloaders() {
        this.content.querySelectorAll('.product__media img').forEach((img) => {
          if (img.complete && img.naturalWidth > 0) {
            this.markImageLoaded(img);
          }
        });
      }

      markImageLoaded(img) {
        img.closest('.product__media')?.classList.add('is-image-loaded');
      }
    }
  );
}
